import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader, EmptyState, Stat } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { fPct, fDateTime } from "@/lib/format";
import { ClipboardList, Users, Layers, HeartPulse, Send, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function MyDepartment() {
  const { data, loading, reload } = useApi("/scope/overview");
  const { data: shiftLoad } = useApi("/rotation/shifts");

  if (loading) return <p className="text-muted-foreground">Laddar…</p>;
  if (!data) return null;

  const publishTeam = async (teamId) => {
    try { const r = await api.post(`/teams/${teamId}/publish-schedule`); toast.success(`${r.data.published} pass godkända och skickade individuellt`); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const empsByDept = (deptId) => (data.employees || []).filter((e) => e.department_id === deptId);
  const empsByTeam = (teamId) => (data.employees || []).filter((e) => e.team_id === teamId);
  const noScope = (data.departments || []).length === 0 && (data.teams || []).length === 0 && (data.employees || []).length === 0;

  return (
    <div>
      <PageHeader title="Min avdelning & grupp" icon={ClipboardList}
        subtitle="Dina ansvarsområden: se vilka som ska vara på avdelningen, sjukanmälningar som berör dig, och godkänn gruppens schema så det skickas individuellt." />

      <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Avdelningar" value={(data.departments || []).length} icon={Layers} />
        <Stat label="Team / grupper" value={(data.teams || []).length} icon={Users} tone="accent" />
        <Stat label="Medarbetare" value={(data.employees || []).length} icon={Users} />
        <Stat label="Sjukanmälda" value={(data.sick_active || []).length} tone={(data.sick_active || []).length ? "danger" : "ok"} icon={HeartPulse} />
      </div>

      {(data.sick_active || []).length > 0 && (
        <Card className="mb-4 border-red-200 bg-red-50 p-4">
          <div className="mb-2 flex items-center gap-2 font-semibold text-red-800"><HeartPulse className="h-5 w-5" />Sjukanmälningar som berör din avdelning</div>
          <div className="space-y-1">
            {data.sick_active.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" data-testid={`scope-sick-${i}`}>
                <span className="font-medium">{s.employee_name}</span>
                <span className="text-muted-foreground">— sjuk från {fDateTime(s.start_time)}. Bemanningskontroll genomförd.</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {noScope ? (
        <EmptyState icon={ClipboardList} title="Inga ansvarsområden ännu"
          text="Koppla dig som ansvarig på en avdelning eller ett team under Organisation, så visas dina medarbetare och ärenden här." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Avdelningar */}
          <div>
            <h2 className="mb-2 font-head text-lg font-semibold">Avdelning – vilka ska vara här</h2>
            <div className="space-y-2">
              {(data.departments || []).map((d) => (
                <Card key={d.id} className="p-4" data-testid={`scope-dep-${d.id}`}>
                  <div className="mb-2 flex items-center gap-2"><Layers className="h-4 w-4 text-accent" /><span className="font-medium">{d.name}</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {empsByDept(d.id).length === 0 ? <span className="text-xs text-muted-foreground">Inga kopplade medarbetare.</span> :
                      empsByDept(d.id).map((e) => (
                        <Badge key={e.id} variant={e.available ? "secondary" : "destructive"} className="gap-1">
                          {e.available ? null : <HeartPulse className="h-3 w-3" />}{e.name}
                        </Badge>
                      ))}
                  </div>
                </Card>
              ))}
              {(data.departments || []).length === 0 && <p className="text-sm text-muted-foreground">Inga avdelningar där du är ansvarig.</p>}
            </div>
          </div>

          {/* Grupper / team */}
          <div>
            <h2 className="mb-2 font-head text-lg font-semibold">Grupp – godkänn & skicka schema</h2>
            <div className="space-y-2">
              {(data.teams || []).map((t) => {
                const pending = data.pending_by_team?.[t.id] || 0;
                return (
                  <Card key={t.id} className="p-4" data-testid={`scope-team-${t.id}`}>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-accent" /><span className="font-medium">{t.name}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{empsByTeam(t.id).length} medarbetare</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      {pending > 0
                        ? <Badge variant="outline" className="gap-1 text-amber-700"><Clock className="h-3 w-3" />{pending} pass väntar på godkännande</Badge>
                        : <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3" />Inget att godkänna</Badge>}
                      <Button size="sm" disabled={pending === 0} onClick={() => publishTeam(t.id)} data-testid={`publish-team-${t.id}`}>
                        <Send className="mr-1 h-4 w-4" />Godkänn & skicka till gruppen
                      </Button>
                    </div>
                  </Card>
                );
              })}
              {(data.teams || []).length === 0 && <p className="text-sm text-muted-foreground">Inga team där du är ansvarig.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Belastning per skift */}
      {(shiftLoad || []).length > 0 && (
        <div className="mt-6">
          <h2 className="mb-2 font-head text-lg font-semibold">Belastning per skift (28 dagar)</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {shiftLoad.map((s) => (
              <Card key={s.shift_id} className="p-4" data-testid={`shiftload-${s.shift_id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{s.shift_name}</span>
                  <StatusPill level={s.level} />
                </div>
                <div className="mt-2 text-sm text-muted-foreground">Tung andel: <span className="font-mono font-bold text-orange-800">{fPct(s.heavy_pct)}</span></div>
                <div className="text-xs text-muted-foreground">Datatäckning: {fPct(s.data_coverage_pct)}</div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
